import React, { useState, useEffect } from "react";

import {
  Grid,
  Box,
  TextField,
  Typography,
  Paper,
  Pagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Skeleton,
  Container,
  Stack
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import ProductCard from "../components/ProductCard";

import { productApi } from "../services/api";

import { useCart } from "../context/CartContext";

const sortOptions = [
  "Newest",
  "Price Low to High",
  "Price High to Low"
];

const PLP = () => {
  const [allProducts, setAllProducts] =
    useState([]);

  const [products, setProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [searchTerm, setSearchTerm] =
    useState("");

  const [sortBy, setSortBy] =
    useState("Newest");

  const [currentPage, setCurrentPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const itemsPerPage = 12;

  const { cart, addToCart, updateQuantity } =
    useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [
    allProducts,
    searchTerm,
    sortBy,
    currentPage
  ]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const response =
        await productApi.getAll();

      setAllProducts(response.data || []);
    } catch (err) {
      console.log(err);

      setError(
        "Unable to load products."
      );
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...allProducts];

    if (searchTerm) {
      filtered = filtered.filter((p) =>
        p.name
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          )
      );
    }

    if (sortBy === "Price Low to High") {
      filtered.sort(
        (a, b) => a.price - b.price
      );
    }

    if (sortBy === "Price High to Low") {
      filtered.sort(
        (a, b) => b.price - a.price
      );
    }

    setTotalPages(
      Math.ceil(
        filtered.length / itemsPerPage
      )
    );

    const start =
      (currentPage - 1) * itemsPerPage;

    const end = start + itemsPerPage;

    setProducts(filtered.slice(start, end));
  };

  const handlePageChange = (
    _,
    value
  ) => {
    setCurrentPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <Box
      sx={{
        background: "#fffaf7",
        minHeight: "100vh",
        pb: 10
      }}
    >
      {/* TOP SECTION */}
      <Box
        sx={{
          background:
            "linear-gradient(to bottom,#fff5f1,#fffaf7)",
          borderBottom:
            "1px solid rgba(139,0,0,0.06)"
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: {
              xs: 5,
              md: 7
            }
          }}
        >
          {/* BREADCRUMB */}
          <Typography
            sx={{
              fontSize: 13,
              color: "rgba(0,0,0,0.45)",
              fontWeight: 600,
              mb: 2
            }}
          >
            Home / Collection / Masalas
          </Typography>

          {/* TITLE */}
          <Typography
            sx={{
              fontSize: {
                xs: "2.2rem",
                md: "4rem"
              },
              fontWeight: 900,
              color: "#5c0000",
              lineHeight: 1.05,
              mb: 2,
              letterSpacing: "-1px"
            }}
          >
            Premium Indian
            <br />
            Masalas
          </Typography>

          <Typography
            sx={{
              color: "rgba(0,0,0,0.62)",
              maxWidth: 700,
              lineHeight: 1.9,
              fontSize: {
                xs: 15,
                md: 17
              },
              mb: 5
            }}
          >
            Crafted with purity, rich aroma
            and authentic Indian recipes to
            elevate everyday cooking.
          </Typography>

          {/* SEARCH + SORT */}
          <Stack
            direction={{
              xs: "column",
              md: "row"
            }}
            spacing={2}
          >
            <TextField
              fullWidth
              placeholder="Search your favorite masalas..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(
                  e.target.value
                );

                setCurrentPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color:
                          "#8B0000"
                      }}
                    />
                  </InputAdornment>
                )
              }}
              sx={{
                flex: 1,

                "& .MuiOutlinedInput-root":
                  {
                    height: 60,
                    borderRadius:
                      "18px",
                    background:
                      "#fff",
                    fontSize: 15,

                    "& fieldset": {
                      border:
                        "1px solid rgba(139,0,0,0.12)"
                    },

                    "&:hover fieldset":
                      {
                        borderColor:
                          "#8B0000"
                      },

                    "&.Mui-focused fieldset":
                      {
                        borderColor:
                          "#8B0000"
                      }
                  }
              }}
            />

            <FormControl
              sx={{
                width: {
                  xs: "100%",
                  md: 260
                }
              }}
            >
              <Select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(
                    e.target.value
                  )
                }
                sx={{
                  height: 60,
                  borderRadius: "18px",
                  background: "#fff",

                  "& fieldset": {
                    border:
                      "1px solid rgba(139,0,0,0.12)"
                  }
                }}
              >
                {sortOptions.map(
                  (option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Container>
      </Box>

      {/* PRODUCT SECTION */}
      <Container
        maxWidth="xl"
        sx={{
          pt: 5
        }}
      >
        {/* TOP BAR */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography
            sx={{
              fontWeight: 800,
              color: "#222",
              fontSize: {
                xs: "1.3rem",
                md: "1.7rem"
              }
            }}
          >
            All Products
          </Typography>

          <Typography
            sx={{
              color:
                "rgba(0,0,0,0.55)",
              fontWeight: 600
            }}
          >
            {allProducts.length} Products
          </Typography>
        </Box>

        {/* ERROR */}
        {error && (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: "18px",
              background: "#fff1f1"
            }}
          >
            <Typography
              color="error"
              fontWeight={700}
            >
              {error}
            </Typography>
          </Paper>
        )}

        {/* LOADING */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map(
              (_, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  lg={3}
                  key={index}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius:
                        "24px",
                      background:
                        "#fff"
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={280}
                      sx={{
                        borderRadius:
                          "18px",
                        mb: 2
                      }}
                    />

                    <Skeleton
                      height={35}
                    />

                    <Skeleton
                      width="60%"
                    />
                  </Paper>
                </Grid>
              )
            )}
          </Grid>
        ) : (
          <>
            {/* GRID */}
            <Grid
              container
              spacing={{
                xs: 2,
                md: 3
              }}
            >
              {products.map((product) => {
                const cartItem =
                  cart.find(
                    (item) =>
                      item._id ===
                      product._id
                  );

                return (
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={4}
                    lg={3}
                    key={product._id}
                  >
                    <ProductCard
                      product={product}
                      cartItem={cartItem}
                      addToCart={
                        addToCart
                      }
                      updateQuantity={
                        updateQuantity
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>

            {/* EMPTY */}
            {products.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 8,
                  mt: 3,
                  borderRadius: "30px",
                  textAlign: "center",
                  background: "#fff"
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={800}
                  mb={2}
                >
                  No Products Found
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Try another search keyword.
                </Typography>
              </Paper>
            )}

            {/* PAGINATION */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                mt={8}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={
                    handlePageChange
                  }
                  shape="rounded"
                  size="large"
                  sx={{
                    "& .MuiPaginationItem-root":
                      {
                        background:
                          "#fff"
                      },

                    "& .Mui-selected":
                      {
                        background:
                          "#8B0000 !important",
                        color:
                          "#fff"
                      }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PLP;